/**
 * RichTextEditor — TipTap-powered composer used by the community
 * contributor form and (in future) the AI Content Engine editorial UI.
 *
 * Features:
 *   • Bold, italic, underline, strikethrough
 *   • Headings (H2/H3) as size choices
 *   • Font family picker (4 curated publishing fonts)
 *   • Bullet + numbered lists, blockquote
 *   • Link with URL prompt
 *   • Image upload + align (left/center/right) with CSS float text-wrap
 *   • YouTube embed
 *   • Text align (left/center/right/justify)
 *
 * Emits HTML via `onChange(html)` — the parent stores it in `story_html`.
 */
import { useCallback } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import LinkExt from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import Youtube from "@tiptap/extension-youtube";
import { TextStyle } from "@tiptap/extension-text-style";
import { FontFamily } from "@tiptap/extension-font-family";
import { Underline } from "@tiptap/extension-underline";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  Heading2, Heading3, List, ListOrdered, Quote,
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Link as LinkIcon, Image as ImageIcon, Youtube as YoutubeIcon, Undo2, Redo2,
} from "lucide-react";

const FONTS = [
  { key: "'Lora', Georgia, serif",             label: "Lora (serif)" },
  { key: "'Merriweather', Georgia, serif",     label: "Merriweather" },
  { key: "'Inter', system-ui, sans-serif",     label: "Inter (sans)" },
  { key: "'IBM Plex Mono', ui-monospace, monospace", label: "IBM Plex Mono" },
];

function ToolbarBtn({ active, disabled, onClick, title, children, testId }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={`p-1.5 rounded-md text-sm hover:bg-slate-100 disabled:opacity-30 transition-colors ${
        active ? "bg-slate-200 text-slate-900" : "text-slate-600"
      }`}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

export default function RichTextEditor({
  value = "",
  onChange,
  placeholder = "Write your story…",
  onUploadImage,   // async (File) => url
  testId = "rich-text-editor",
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Underline,
      TextStyle,
      FontFamily.configure({ types: ["textStyle"] }),
      LinkExt.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
      Image.configure({
        inline: false, allowBase64: false,
        HTMLAttributes: { class: "rte-image" },
      }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Youtube.configure({ width: 560, height: 315, allowFullscreen: true, HTMLAttributes: { class: "rte-youtube" } }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange && onChange(editor.getHTML()),
  });

  const onImage = useCallback(async () => {
    if (!editor || !onUploadImage) return;
    const input = document.createElement("input");
    input.type = "file"; input.accept = "image/*";
    input.onchange = async () => {
      const file = input.files?.[0]; if (!file) return;
      try {
        const url = await onUploadImage(file);
        if (url) editor.chain().focus().setImage({ src: url }).run();
      } catch (e) { /* silent */ }
    };
    input.click();
  }, [editor, onUploadImage]);

  const setImageAlign = (align) => {
    if (!editor) return;
    editor.chain().focus()
      .updateAttributes("image", { class: `rte-image rte-image-${align}` })
      .run();
  };

  const promptLink = () => {
    const prev = editor?.getAttributes("link").href;
    const url = window.prompt("Link URL", prev || "https://");
    if (url === null) return;
    if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const promptYouTube = () => {
    const url = window.prompt("YouTube URL");
    if (!url) return;
    editor.chain().focus().setYoutubeVideo({ src: url }).run();
  };

  if (!editor) return <div className="p-4 text-sm text-slate-500">Loading editor…</div>;

  return (
    <div className="rounded-xl border border-slate-300 bg-white overflow-hidden" data-testid={testId}>
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-200 bg-slate-50 px-2 py-1.5">
        <select
          onChange={(e) => e.target.value && editor.chain().focus().setFontFamily(e.target.value).run()}
          value={editor.getAttributes("textStyle").fontFamily || ""}
          className="text-xs bg-white border border-slate-200 rounded-md px-2 py-1 mr-1"
          data-testid="rte-font-family"
          title="Font family"
        >
          <option value="">Default font</option>
          {FONTS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
        </select>

        <ToolbarBtn testId="rte-h2" title="Heading (large)"
          active={editor.isActive("heading", { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId="rte-h3" title="Sub-heading"
          active={editor.isActive("heading", { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Heading3 className="w-4 h-4" /></ToolbarBtn>

        <span className="w-px h-5 bg-slate-300 mx-1" />

        <ToolbarBtn testId="rte-bold" title="Bold" active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}><Bold className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId="rte-italic" title="Italic" active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}><Italic className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId="rte-underline" title="Underline" active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}><UnderlineIcon className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId="rte-strike" title="Strikethrough" active={editor.isActive("strike")}
          onClick={() => editor.chain().focus().toggleStrike().run()}><Strikethrough className="w-4 h-4" /></ToolbarBtn>

        <span className="w-px h-5 bg-slate-300 mx-1" />

        <ToolbarBtn testId="rte-bullet" title="Bullet list" active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}><List className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId="rte-ol" title="Numbered list" active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId="rte-quote" title="Blockquote" active={editor.isActive("blockquote")}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote className="w-4 h-4" /></ToolbarBtn>

        <span className="w-px h-5 bg-slate-300 mx-1" />

        <ToolbarBtn testId="rte-align-l" title="Align left" active={editor.isActive({ textAlign: "left" })}
          onClick={() => editor.chain().focus().setTextAlign("left").run()}><AlignLeft className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId="rte-align-c" title="Center" active={editor.isActive({ textAlign: "center" })}
          onClick={() => editor.chain().focus().setTextAlign("center").run()}><AlignCenter className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId="rte-align-r" title="Align right" active={editor.isActive({ textAlign: "right" })}
          onClick={() => editor.chain().focus().setTextAlign("right").run()}><AlignRight className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId="rte-align-j" title="Justify" active={editor.isActive({ textAlign: "justify" })}
          onClick={() => editor.chain().focus().setTextAlign("justify").run()}><AlignJustify className="w-4 h-4" /></ToolbarBtn>

        <span className="w-px h-5 bg-slate-300 mx-1" />

        <ToolbarBtn testId="rte-link" title="Link" active={editor.isActive("link")}
          onClick={promptLink}><LinkIcon className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId="rte-image" title="Insert image" onClick={onImage}><ImageIcon className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId="rte-youtube" title="Embed YouTube" onClick={promptYouTube}><YoutubeIcon className="w-4 h-4" /></ToolbarBtn>

        {editor.isActive("image") && (
          <>
            <span className="w-px h-5 bg-slate-300 mx-1" />
            <span className="text-[10px] uppercase tracking-widest text-slate-500 mr-1">Image</span>
            <ToolbarBtn testId="rte-img-left" title="Image left + wrap"
              onClick={() => setImageAlign("left")}><AlignLeft className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn testId="rte-img-center" title="Image centered"
              onClick={() => setImageAlign("center")}><AlignCenter className="w-4 h-4" /></ToolbarBtn>
            <ToolbarBtn testId="rte-img-right" title="Image right + wrap"
              onClick={() => setImageAlign("right")}><AlignRight className="w-4 h-4" /></ToolbarBtn>
          </>
        )}

        <span className="w-px h-5 bg-slate-300 mx-1" />

        <ToolbarBtn testId="rte-undo" title="Undo" disabled={!editor.can().undo()}
          onClick={() => editor.chain().focus().undo().run()}><Undo2 className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn testId="rte-redo" title="Redo" disabled={!editor.can().redo()}
          onClick={() => editor.chain().focus().redo().run()}><Redo2 className="w-4 h-4" /></ToolbarBtn>
      </div>

      {/* Editable surface — story-like typography */}
      <div className="rte-body px-4 py-3 min-h-[240px] max-h-[560px] overflow-auto prose prose-slate max-w-none focus-within:outline-none">
        <EditorContent editor={editor} />
      </div>

      {/* Inline CSS — image align + wrap. Kept scoped so it doesn't leak. */}
      <style>{`
        .rte-body :focus-visible { outline: none; }
        .rte-body p, .rte-body h2, .rte-body h3 { line-height: 1.55; }
        .rte-body img.rte-image { max-width: 100%; height: auto; margin: 0.5rem 0; border-radius: 0.5rem; }
        .rte-body img.rte-image-left  { float: left;  margin: 0.25rem 1rem 0.5rem 0; max-width: 50%; }
        .rte-body img.rte-image-right { float: right; margin: 0.25rem 0 0.5rem 1rem; max-width: 50%; }
        .rte-body img.rte-image-center { display: block; margin: 0.75rem auto; max-width: 90%; float: none; }
        .rte-body p::after { content: ""; display: table; clear: both; }
        .rte-body iframe.rte-youtube { max-width: 100%; margin: 1rem 0; border-radius: 0.5rem; }
        .rte-body .is-editor-empty:first-child::before {
          content: attr(data-placeholder); color: #94a3b8; pointer-events: none; height: 0; float: left;
        }
      `}</style>
    </div>
  );
}
